"use strict";
/**
 * Access Control Service
 * Handles user access management, role assignments, and access requests
 * Works across web and native platforms using unified Firestore service
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.accessControlService = void 0;
const firestore_1 = require("firebase/firestore");
const constants_1 = require("../constants");
const unifiedFirestoreService_1 = require("./unifiedFirestoreService");
class AccessControlService {
    /**
     * Ensure access_requests has a pending row for this user (creates, or reopens approved/rejected).
     * Skips if the document is already pending.
     */
    upsertPendingAccessRequest(uid, email, displayName, photoURL, platform) {
        return __awaiter(this, void 0, void 0, function* () {
            const existing = yield this.getAccessRequest(uid);
            if ((existing === null || existing === void 0 ? void 0 : existing.status) === constants_1.ACCESS_REQUEST_STATUS.PENDING) {
                return;
            }
            const payload = {
                id: uid,
                uid,
                email: email || '',
                displayName: displayName || 'User',
                status: constants_1.ACCESS_REQUEST_STATUS.PENDING,
                requestedAt: firestore_1.Timestamp.now(),
            };
            if (photoURL !== undefined) {
                payload.photoURL = photoURL;
            }
            if (platform !== undefined) {
                payload.platform = platform;
            }
            yield (0, unifiedFirestoreService_1.unifiedSetDoc)(constants_1.COLLECTIONS.ACCESS_REQUESTS, uid, payload, true);
        });
    }
    /**
     * Check if a user is a founding admin (cannot be demoted)
     */
    isFoundingAdmin(uid) {
        return constants_1.FOUNDING_ADMIN_UIDS.includes(uid);
    }
    /**
     * Check if a user has admin role
     */
    isUserAdmin(uid) {
        return __awaiter(this, void 0, void 0, function* () {
            // Founding admins are always admins
            if (this.isFoundingAdmin(uid)) {
                return true;
            }
            try {
                const profileDoc = yield (0, unifiedFirestoreService_1.unifiedGetDoc)(constants_1.COLLECTIONS.USER_PROFILES, uid);
                if (profileDoc.exists()) {
                    const data = profileDoc.data();
                    return (data === null || data === void 0 ? void 0 : data.role) === constants_1.USER_ROLES.ADMIN;
                }
                return false;
            }
            catch (error) {
                console.error('❌ AccessControlService: Error checking admin status:', error);
                return false;
            }
        });
    }
    /**
     * Get user's current role
     */
    getUserRole(uid) {
        return __awaiter(this, void 0, void 0, function* () {
            // Founding admins are always admins
            if (this.isFoundingAdmin(uid)) {
                return constants_1.USER_ROLES.ADMIN;
            }
            try {
                const { getWaitlistEnabled } = yield Promise.resolve().then(() => __importStar(require('../constants/index')));
                const profileDoc = yield (0, unifiedFirestoreService_1.unifiedGetDoc)(constants_1.COLLECTIONS.USER_PROFILES, uid);
                if (profileDoc.exists()) {
                    const data = profileDoc.data();
                    const role = (data === null || data === void 0 ? void 0 : data.role) || constants_1.USER_ROLES.REGULAR;
                    // If waitlist is disabled and user is stuck as pending, upgrade them silently.
                    if (!getWaitlistEnabled() && role === constants_1.USER_ROLES.PENDING) {
                        try {
                            yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)(constants_1.COLLECTIONS.USER_PROFILES, uid, { role: constants_1.USER_ROLES.REGULAR });
                            yield (0, unifiedFirestoreService_1.unifiedDeleteDoc)('accessRequests', uid);
                        }
                        catch (_) { }
                        return constants_1.USER_ROLES.REGULAR;
                    }
                    return role;
                }
                // User doesn't exist yet — pending only if waitlist is enabled
                return getWaitlistEnabled() ? constants_1.USER_ROLES.PENDING : constants_1.USER_ROLES.REGULAR;
            }
            catch (error) {
                console.error('❌ AccessControlService: Error getting user role:', error);
                return constants_1.USER_ROLES.PENDING;
            }
        });
    }
    /**
     * Check if user has access to the app (not pending)
     */
    hasAccess(uid) {
        return __awaiter(this, void 0, void 0, function* () {
            const role = yield this.getUserRole(uid);
            return role !== constants_1.USER_ROLES.PENDING;
        });
    }
    /**
     * Create an access request for a new user
     */
    createAccessRequest(uid, email, displayName, photoURL, platform) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const existingRequest = yield this.getAccessRequest(uid);
                if ((existingRequest === null || existingRequest === void 0 ? void 0 : existingRequest.status) === constants_1.ACCESS_REQUEST_STATUS.PENDING) {
                    console.log('📋 AccessControlService: Pending access request already exists for user:', uid);
                    return existingRequest.id;
                }
                yield this.upsertPendingAccessRequest(uid, email, displayName, photoURL, platform);
                console.log('✅ AccessControlService: Access request created or reopened for user:', uid);
                return uid;
            }
            catch (error) {
                console.error('❌ AccessControlService: Error creating access request:', error);
                throw error;
            }
        });
    }
    /**
     * Get access request for a user
     */
    getAccessRequest(uid) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const doc = yield (0, unifiedFirestoreService_1.unifiedGetDoc)(constants_1.COLLECTIONS.ACCESS_REQUESTS, uid);
                if (doc.exists()) {
                    return doc.data();
                }
                return null;
            }
            catch (error) {
                console.error('❌ AccessControlService: Error getting access request:', error);
                return null;
            }
        });
    }
    /**
     * Get all pending access requests (admin only)
     */
    getPendingRequests() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const fetchPendingFromCollection = () => __awaiter(this, void 0, void 0, function* () {
                    const q = (0, unifiedFirestoreService_1.unifiedCollection)(constants_1.COLLECTIONS.ACCESS_REQUESTS).where('status', '==', constants_1.ACCESS_REQUEST_STATUS.PENDING);
                    const snapshot = yield q.getDocs();
                    return snapshot.map(doc => doc.data());
                });
                let requests = yield fetchPendingFromCollection();
                const uidsWithPendingDoc = new Set(requests.map(r => r.uid));
                const pendingRoleDocs = yield (0, unifiedFirestoreService_1.unifiedCollection)(constants_1.COLLECTIONS.USER_PROFILES)
                    .where('role', '==', constants_1.USER_ROLES.PENDING)
                    .getDocs();
                let healed = false;
                for (const doc of pendingRoleDocs) {
                    const profile = doc.data();
                    if (!uidsWithPendingDoc.has(profile.uid)) {
                        yield this.upsertPendingAccessRequest(profile.uid, profile.email, profile.displayName, profile.photoURL);
                        healed = true;
                    }
                }
                if (healed) {
                    requests = yield fetchPendingFromCollection();
                }
                return requests.sort((a, b) => {
                    var _a, _b, _c, _d, _e, _f;
                    const timeA = ((_a = a.requestedAt) === null || _a === void 0 ? void 0 : _a.toMillis)
                        ? a.requestedAt.toMillis()
                        : ((_b = a.requestedAt) === null || _b === void 0 ? void 0 : _b._seconds) * 1000 ||
                            ((_c = a.requestedAt) === null || _c === void 0 ? void 0 : _c.seconds) * 1000 ||
                            0;
                    const timeB = ((_d = b.requestedAt) === null || _d === void 0 ? void 0 : _d.toMillis)
                        ? b.requestedAt.toMillis()
                        : ((_e = b.requestedAt) === null || _e === void 0 ? void 0 : _e._seconds) * 1000 ||
                            ((_f = b.requestedAt) === null || _f === void 0 ? void 0 : _f.seconds) * 1000 ||
                            0;
                    return timeB - timeA;
                });
            }
            catch (error) {
                console.error('❌ AccessControlService: Error getting pending requests:', error);
                return [];
            }
        });
    }
    /**
     * Get all access requests (admin only)
     */
    getAllRequests() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const query = (0, unifiedFirestoreService_1.unifiedCollection)(constants_1.COLLECTIONS.ACCESS_REQUESTS);
                const docs = yield query.getDocs();
                const requests = docs.map(doc => doc.data());
                // Client-side sort
                return requests.sort((a, b) => {
                    var _a, _b, _c, _d, _e, _f;
                    const timeA = ((_a = a.requestedAt) === null || _a === void 0 ? void 0 : _a.toMillis) ? a.requestedAt.toMillis() : ((_b = a.requestedAt) === null || _b === void 0 ? void 0 : _b._seconds) * 1000 || ((_c = a.requestedAt) === null || _c === void 0 ? void 0 : _c.seconds) * 1000 || 0;
                    const timeB = ((_d = b.requestedAt) === null || _d === void 0 ? void 0 : _d.toMillis) ? b.requestedAt.toMillis() : ((_e = b.requestedAt) === null || _e === void 0 ? void 0 : _e._seconds) * 1000 || ((_f = b.requestedAt) === null || _f === void 0 ? void 0 : _f.seconds) * 1000 || 0;
                    return timeB - timeA;
                });
            }
            catch (error) {
                console.error('❌ AccessControlService: Error getting all requests:', error);
                return [];
            }
        });
    }
    /**
     * Approve an access request (admin only)
     */
    approveRequest(requestId, adminUid) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Verify admin
                const isAdmin = yield this.isUserAdmin(adminUid);
                if (!isAdmin) {
                    throw new Error('Unauthorized: Only admins can approve requests');
                }
                // Get the request
                const request = yield this.getAccessRequest(requestId);
                if (!request) {
                    throw new Error('Access request not found');
                }
                // Update the request status
                yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)(constants_1.COLLECTIONS.ACCESS_REQUESTS, requestId, {
                    status: constants_1.ACCESS_REQUEST_STATUS.APPROVED,
                    reviewedBy: adminUid,
                    reviewedAt: firestore_1.Timestamp.now(),
                });
                // Update the user's role to 'regular'
                yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)(constants_1.COLLECTIONS.USER_PROFILES, request.uid, {
                    role: constants_1.USER_ROLES.REGULAR,
                    roleChangedAt: firestore_1.Timestamp.now(),
                    roleChangedBy: adminUid,
                    updatedAt: firestore_1.Timestamp.now(),
                });
                console.log('✅ AccessControlService: Approved request for user:', request.uid);
            }
            catch (error) {
                console.error('❌ AccessControlService: Error approving request:', error);
                throw error;
            }
        });
    }
    /**
     * Reject an access request (admin only)
     */
    rejectRequest(requestId, adminUid, note) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Verify admin
                const isAdmin = yield this.isUserAdmin(adminUid);
                if (!isAdmin) {
                    throw new Error('Unauthorized: Only admins can reject requests');
                }
                // Get the request
                const request = yield this.getAccessRequest(requestId);
                if (!request) {
                    throw new Error('Access request not found');
                }
                // Update the request status
                yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)(constants_1.COLLECTIONS.ACCESS_REQUESTS, requestId, {
                    status: constants_1.ACCESS_REQUEST_STATUS.REJECTED,
                    reviewedBy: adminUid,
                    reviewedAt: firestore_1.Timestamp.now(),
                    reviewNote: note || null,
                });
                console.log('✅ AccessControlService: Rejected request for user:', request.uid);
            }
            catch (error) {
                console.error('❌ AccessControlService: Error rejecting request:', error);
                throw error;
            }
        });
    }
    /**
     * Update a user's role (admin only)
     */
    updateUserRole(targetUid, newRole, adminUid) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Verify admin
                const isAdmin = yield this.isUserAdmin(adminUid);
                if (!isAdmin) {
                    throw new Error('Unauthorized: Only admins can change user roles');
                }
                // Cannot change founding admin roles
                if (this.isFoundingAdmin(targetUid) && newRole !== constants_1.USER_ROLES.ADMIN) {
                    throw new Error('Cannot change role of founding admin');
                }
                // Cannot demote yourself if you're the last admin
                if (targetUid === adminUid && newRole !== constants_1.USER_ROLES.ADMIN) {
                    const allAdmins = yield this.getAllAdmins();
                    if (allAdmins.length <= 1) {
                        throw new Error('Cannot demote yourself: you are the only admin');
                    }
                }
                yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)(constants_1.COLLECTIONS.USER_PROFILES, targetUid, {
                    role: newRole,
                    roleChangedAt: firestore_1.Timestamp.now(),
                    roleChangedBy: adminUid,
                    updatedAt: firestore_1.Timestamp.now(),
                });
                if (newRole === constants_1.USER_ROLES.PENDING) {
                    const profileDoc = yield (0, unifiedFirestoreService_1.unifiedGetDoc)(constants_1.COLLECTIONS.USER_PROFILES, targetUid);
                    if (profileDoc.exists()) {
                        const p = profileDoc.data();
                        yield this.upsertPendingAccessRequest(targetUid, p.email, p.displayName, p.photoURL);
                    }
                }
                console.log(`✅ AccessControlService: Updated role for ${targetUid} to ${newRole}`);
            }
            catch (error) {
                console.error('❌ AccessControlService: Error updating user role:', error);
                throw error;
            }
        });
    }
    /**
     * Get all users with their roles (admin only)
     */
    getAllUsersWithRoles(limitCount = 100) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const query = (0, unifiedFirestoreService_1.unifiedCollection)(constants_1.COLLECTIONS.USER_PROFILES)
                    .orderBy('createdAt', 'desc')
                    .limit(limitCount);
                const docs = yield query.getDocs();
                return docs.map(doc => {
                    const data = doc.data();
                    // Default role for existing users without a role field
                    if (!data.role) {
                        data.role = constants_1.USER_ROLES.REGULAR;
                    }
                    return data;
                });
            }
            catch (error) {
                console.error('❌ AccessControlService: Error getting all users:', error);
                return [];
            }
        });
    }
    /**
     * Get all admins
     */
    getAllAdmins() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const query = (0, unifiedFirestoreService_1.unifiedCollection)(constants_1.COLLECTIONS.USER_PROFILES)
                    .where('role', '==', constants_1.USER_ROLES.ADMIN);
                const docs = yield query.getDocs();
                const admins = docs.map(doc => doc.data());
                // Also include founding admins who might not have the role field set yet
                for (const foundingAdminUid of constants_1.FOUNDING_ADMIN_UIDS) {
                    const existingAdmin = admins.find(a => a.uid === foundingAdminUid);
                    if (!existingAdmin) {
                        const profileDoc = yield (0, unifiedFirestoreService_1.unifiedGetDoc)(constants_1.COLLECTIONS.USER_PROFILES, foundingAdminUid);
                        if (profileDoc.exists()) {
                            admins.push(profileDoc.data());
                        }
                    }
                }
                return admins;
            }
            catch (error) {
                console.error('❌ AccessControlService: Error getting admins:', error);
                return [];
            }
        });
    }
    /**
     * Search users by email or display name (admin only)
     */
    searchUsers(searchTerm) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Note: Firestore doesn't support full-text search
                // This is a basic implementation that gets all users and filters client-side
                // For production, consider using Algolia or Elasticsearch
                const allUsers = yield this.getAllUsersWithRoles(500);
                const lowerSearch = searchTerm.toLowerCase();
                return allUsers.filter(user => {
                    var _a, _b, _c;
                    return ((_a = user.email) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(lowerSearch)) ||
                        ((_b = user.displayName) === null || _b === void 0 ? void 0 : _b.toLowerCase().includes(lowerSearch)) ||
                        ((_c = user.username) === null || _c === void 0 ? void 0 : _c.toLowerCase().includes(lowerSearch));
                });
            }
            catch (error) {
                console.error('❌ AccessControlService: Error searching users:', error);
                return [];
            }
        });
    }
    /**
     * Get detailed stats for a specific user (admin only)
     */
    getUserDetailedStats(uid) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Get user's created watchlists (collection is 'watchlists', not 'userWatchlists')
                const createdWatchlists = yield (0, unifiedFirestoreService_1.unifiedCollection)('watchlists')
                    .where('createdBy', '==', uid)
                    .getDocs();
                // Calculate total likes on user's watchlists
                let totalWatchlistLikes = 0;
                createdWatchlists.forEach(doc => {
                    const data = doc.data();
                    totalWatchlistLikes += data.followerCount || 0;
                });
                // Get watchlists user has liked
                const likedWatchlists = yield (0, unifiedFirestoreService_1.unifiedCollection)('watchlistLikes')
                    .where('userUid', '==', uid)
                    .getDocs();
                // Get user movie data (uses 'uid' field)
                const userMovieData = yield (0, unifiedFirestoreService_1.unifiedCollection)(constants_1.COLLECTIONS.USER_MOVIES)
                    .where('uid', '==', uid)
                    .getDocs();
                let moviesWatched = 0;
                let moviesWatchlisted = 0;
                let reviewsWritten = 0;
                userMovieData.forEach(doc => {
                    const data = doc.data();
                    if (data.isWatched)
                        moviesWatched++;
                    if (data.isInWatchlist)
                        moviesWatchlisted++;
                    if (data.review && data.review.trim())
                        reviewsWritten++;
                });
                // Get follower/following counts from user profile
                const profileDoc = yield (0, unifiedFirestoreService_1.unifiedGetDoc)(constants_1.COLLECTIONS.USER_PROFILES, uid);
                const profileData = profileDoc.exists() ? profileDoc.data() : null;
                const stats = (profileData === null || profileData === void 0 ? void 0 : profileData.stats) || {};
                return {
                    watchlistsCreated: createdWatchlists.length,
                    watchlistsLiked: likedWatchlists.length,
                    totalWatchlistLikes,
                    moviesWatched,
                    moviesWatchlisted,
                    reviewsWritten,
                    followersCount: stats.followersCount || 0,
                    followingCount: stats.followingCount || 0,
                };
            }
            catch (error) {
                console.error('❌ AccessControlService: Error getting user detailed stats:', error);
                return {
                    watchlistsCreated: 0,
                    watchlistsLiked: 0,
                    totalWatchlistLikes: 0,
                    moviesWatched: 0,
                    moviesWatchlisted: 0,
                    reviewsWritten: 0,
                    followersCount: 0,
                    followingCount: 0,
                };
            }
        });
    }
    /**
     * Get counts for admin dashboard
     */
    getAccessStats() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const [allUsers, pendingRequests, admins] = yield Promise.all([
                    this.getAllUsersWithRoles(1000),
                    this.getPendingRequests(),
                    this.getAllAdmins(),
                ]);
                const testUsers = allUsers.filter(u => u.role === constants_1.USER_ROLES.TEST).length;
                return {
                    totalUsers: allUsers.length,
                    pendingRequests: pendingRequests.length,
                    admins: admins.length,
                    testUsers,
                };
            }
            catch (error) {
                console.error('❌ AccessControlService: Error getting stats:', error);
                return {
                    totalUsers: 0,
                    pendingRequests: 0,
                    admins: 0,
                    testUsers: 0,
                };
            }
        });
    }
}
exports.accessControlService = new AccessControlService();
